import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef } from "preact/hooks";
import { Button } from "./Button.tsx";
import { Icon } from "./Icon.tsx";
import type { IconName } from "./Icon.tsx";

export interface DialogRailItem {
  label: string; icon?: IconName; current?: boolean; onSelect?: () => void;
  /** Why it can't be opened yet. The item stays in the rail, dimmed, with this as its hint. */
  unavailable?: string;
}

// See Design > UI, "Curator Studio assets". Native modal supplies focus containment and Escape.
// `wide` is for a settings-style dialog: up to 960px, with an optional `rail` of sections on its
// left that stacks above the content on phones. `description` is one line of inline content.
export function Dialog({ open, onClose, title, description, children, actions, size = "default", rail }: {
  open: boolean; onClose: () => void; title: ComponentChildren; description: ComponentChildren;
  children?: ComponentChildren; actions?: ComponentChildren;
  size?: "default" | "wide"; rail?: { title: string; items: ReadonlyArray<DialogRailItem> };
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!open) { if (dialog.open) dialog.close(); return; }
    const previous = document.activeElement;
    if (!dialog.open) dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
      if (previous instanceof HTMLElement && previous.isConnected) {
        const target = previous.matches(":disabled") ? previous.closest<HTMLElement>("main") : previous;
        target?.focus();
      }
    };
  }, [open]);
  const content = <>
    <div class="ui-dialog__head"><h2 id={`${id}-title`}>{title}</h2>
      <Button variant="ghost" size="sm" icon aria-label="Close dialog" onClick={onClose}><Icon name="close" /></Button>
    </div><p id={`${id}-description`} class="ui-dialog__description">{description}</p>
    {children && <div class="ui-dialog__body">{children}</div>}
    {actions && <div class="ui-dialog__actions">{actions}</div>}
  </>;
  return <dialog ref={ref} class={size === "wide" ? "ui-dialog ui-dialog--wide" : "ui-dialog"} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
    onCancel={event => { event.preventDefault(); close.current(); }}>
    {size === "wide" ? <>{rail && railNav(rail)}<div class="ui-dialog__main">{content}</div></> : content}
  </dialog>;
}

function railNav(rail: { title: string; items: ReadonlyArray<DialogRailItem> }) {
  return <nav class="ui-dialog__rail" aria-label={rail.title}>
    <span class="ui-dialog__rail-title">{rail.title}</span>
    {rail.items.map((item) => {
      const body = <>{item.icon && <Icon name={item.icon} size={16} />}<span>{item.label}</span></>;
      return item.unavailable
        ? <span key={item.label} class="ui-dialog__rail-item" aria-disabled="true" title={item.unavailable}>{body}<span class="ui-sr-only"> ({item.unavailable})</span></span>
        : <button key={item.label} type="button" class="ui-dialog__rail-item" aria-current={item.current ? "page" : undefined} onClick={item.onSelect}>{body}</button>;
    })}
  </nav>;
}
